import { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useAuth } from "src/contexts/auth-provider";
import { Channel, ChannelItemPrice, UpsertChannelItemPriceInput } from "src/interfaces/channel.interface";
import { MenuItem } from "src/interfaces/menu.interface";
import { useChannels, useAddChannel, useUpdateChannel } from "./hooks";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import {
  GET_CHANNEL_ITEM_PRICES_BY_STORE,
  GET_CHANNEL_PRICE_ITEMS_BY_STORE,
} from "src/constants/queries";
import {
  BULK_UPSERT_CHANNEL_ITEM_PRICES,
  DEACTIVATE_CHANNEL_ITEM_PRICE,
} from "src/constants/mutations";
import { convertCentsToDollars, convertDollarToCents, formatCentsToDollarsWithDollarSign } from "src/helpers";
import { failureToast, successToast } from "src/utils/toasts";
import Input, { InputTypeEnum } from "../../components/common/Input";
import FormRowVertical from "../../components/common/FormRowVertical";
import Button from "../../components/common/Button";
import Spinner from "../../components/common/Spinner";
import Toggle from "../../components/common/Toggle";
import OrderDetailsDrawer from "../../components/common/OrderDetailsDrawer";

interface ChannelPriceCategory {
  correlationId: string;
  name?: string;
  items?: MenuItem[];
}

const channelSchema = Yup.object().shape({
  name: Yup.string().required("Channel name is required"),
});

const ChannelTab = () => {
  const [editingChannel, setEditingChannel] = useState<Channel | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [priceDrawerChannel, setPriceDrawerChannel] = useState<Channel | null>(null);
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  const [channelItemPrices, setChannelItemPrices] = useState<ChannelItemPrice[]>([]);
  const [priceDrafts, setPriceDrafts] = useState<Record<string, string>>({});
  const [isLoadingPriceData, setIsLoadingPriceData] = useState(false);
  const [isSavingPrices, setIsSavingPrices] = useState(false);

  const { fetchChannels, channels, isLoadingChannels } = useChannels();
  const { addChannel: addChannelMutation, isAddingChannel } = useAddChannel();
  const { updateChannel: updateChannelMutation, isUpdatingChannel } = useUpdateChannel();
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();

  const storeCorrelationId = hasRestaurant?.correlationId;
  const isChannelLoading = isAddingChannel || isUpdatingChannel;

  const activePriceByItemId = useMemo(() => {
    const map = new Map<string, ChannelItemPrice>();
    if (!priceDrawerChannel) return map;

    channelItemPrices
      .filter((price) => price.channelCorrelationId === priceDrawerChannel.correlationId && price.isActive)
      .forEach((price) => map.set(price.itemCorrelationId, price));
    return map;
  }, [channelItemPrices, priceDrawerChannel]);

  const fetchPriceData = useCallback(async () => {
    if (!storeCorrelationId) return;

    setIsLoadingPriceData(true);
    try {
      const [itemsResponse, pricesResponse] = await Promise.all([
        axBe.post(GRAPHQL_ENDPOINT, {
          query: GET_CHANNEL_PRICE_ITEMS_BY_STORE,
          variables: { storeCorrelationId, activeFilter: "ACTIVE" },
        }),
        axBe.post(GRAPHQL_ENDPOINT, {
          query: GET_CHANNEL_ITEM_PRICES_BY_STORE,
          variables: { storeCorrelationId },
        }),
      ]);

      if (itemsResponse.data?.errors?.length) throw itemsResponse.data.errors[0];
      if (pricesResponse.data?.errors?.length) throw pricesResponse.data.errors[0];

      const categories: ChannelPriceCategory[] = itemsResponse.data?.data?.categoriesByStoreCorrelationId ?? [];
      const flattenedItems = categories.flatMap((category) => category.items ?? []);
      setMenuItems(flattenedItems);
      setChannelItemPrices(pricesResponse.data?.data?.channelItemPricesByStore ?? []);
    } catch (error) {
      console.error("Error fetching channel price data:", error);
      failureToast("Failed to fetch channel item prices");
    } finally {
      setIsLoadingPriceData(false);
    }
  }, [axBe, storeCorrelationId]);

  const formik = useFormik({
    initialValues: {
      name: "",
      isActive: true,
    },
    validationSchema: channelSchema,
    onSubmit: async (_values, { resetForm }) => {
      if (editingChannel) {
        await handleUpdateChannel();
      } else {
        await handleAddChannel();
      }
      resetForm();
    },
    enableReinitialize: true,
  });

  const handleAddChannel = async () => {
    await addChannelMutation(
      {
        name: formik.values.name,
        storeCorrelationId,
      },
      {
        onSuccess: () => {
          fetchChannels();
          setDrawerOpen(false);
          formik.resetForm();
        },
      }
    );
  };

  const handleUpdateChannel = async () => {
    if (!editingChannel) return;

    await updateChannelMutation(
      {
        channelCorrelationId: editingChannel.correlationId,
        name: formik.values.name,
        storeCorrelationId,
        isActive: formik.values.isActive,
      },
      {
        onSuccess: () => {
          fetchChannels();
          setEditingChannel(null);
          setDrawerOpen(false);
        },
      }
    );
  };

  const handleEdit = (channel: Channel) => {
    setEditingChannel(channel);
    formik.setFieldValue("name", channel.name);
    formik.setFieldValue("isActive", channel.isActive ?? true);
    setDrawerOpen(true);
  };

  const openAddDrawer = () => {
    setEditingChannel(null);
    formik.resetForm();
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingChannel(null);
  };

  const openPriceDrawer = async (channel: Channel) => {
    setPriceDrawerChannel(channel);
    if (menuItems.length === 0 || channelItemPrices.length === 0) {
      await fetchPriceData();
    }
  };

  const closePriceDrawer = () => {
    setPriceDrawerChannel(null);
    setPriceDrafts({});
  };

  useEffect(() => {
    if (!priceDrawerChannel) return;

    const nextDrafts: Record<string, string> = {};
    channelItemPrices
      .filter((price) => price.channelCorrelationId === priceDrawerChannel.correlationId && price.isActive)
      .forEach((price) => {
        nextDrafts[price.itemCorrelationId] = convertCentsToDollars(price.priceCents).toFixed(2);
      });
    setPriceDrafts(nextDrafts);
  }, [channelItemPrices, priceDrawerChannel]);

  const handlePriceDraftChange = (itemCorrelationId: string, value: string) => {
    const cleaned = value.replace(/[^\d.]/g, "");
    const parts = cleaned.split(".");
    const nextValue = parts.length > 2 ? `${parts[0]}.${parts.slice(1).join("")}` : cleaned;
    setPriceDrafts((previous) => ({
      ...previous,
      [itemCorrelationId]: nextValue.includes(".")
        ? `${nextValue.split(".")[0]}.${nextValue.split(".")[1].slice(0, 2)}`
        : nextValue,
    }));
  };

  const saveChannelItemPrices = async () => {
    if (!storeCorrelationId || !priceDrawerChannel) return;

    const upserts: UpsertChannelItemPriceInput[] = [];
    const deactivations: string[] = [];

    menuItems.forEach((item) => {
      const itemCorrelationId = item.correlationId;
      const draft = priceDrafts[itemCorrelationId]?.trim();
      const existing = activePriceByItemId.get(itemCorrelationId);
      if (draft) {
        upserts.push({
          storeCorrelationId,
          channelCorrelationId: priceDrawerChannel.correlationId,
          itemCorrelationId,
          priceCents: convertDollarToCents(draft),
        });
      } else if (existing) {
        deactivations.push(itemCorrelationId);
      }
    });

    setIsSavingPrices(true);
    try {
      if (upserts.length > 0) {
        const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
          query: BULK_UPSERT_CHANNEL_ITEM_PRICES,
          variables: { input: { prices: upserts } },
        });
        if (data?.errors?.length) throw data.errors[0];
      }

      for (const itemCorrelationId of deactivations) {
        const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
          query: DEACTIVATE_CHANNEL_ITEM_PRICE,
          variables: {
            input: {
              storeCorrelationId,
              channelCorrelationId: priceDrawerChannel.correlationId,
              itemCorrelationId,
            },
          },
        });
        if (data?.errors?.length) throw data.errors[0];
      }

      await fetchPriceData();
      successToast("Channel item prices have been saved.");
    } catch (error) {
      console.error("Error saving channel item prices:", error);
      failureToast("Failed to save channel item prices");
    } finally {
      setIsSavingPrices(false);
    }
  };

  const isButtonDisabled = !formik.isValid || !formik.dirty;

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  useEffect(() => {
    fetchPriceData();
  }, [fetchPriceData]);

  const formContent = (
    <div className="px-4 py-4">
      <FormRowVertical
        label="Channel Name"
        error={formik.touched.name && formik.errors.name ? formik.errors.name : ""}
      >
        <Input
          type={InputTypeEnum.TEXT}
          placeholder="Enter channel name"
          {...formik.getFieldProps("name")}
        />
      </FormRowVertical>

      {editingChannel && (
        <FormRowVertical label="Status">
          <div className="flex items-center gap-3">
            <Toggle
              checked={formik.values.isActive}
              onChange={(checked) => formik.setFieldValue("isActive", checked)}
            />
            <span className="text-[14px] font-medium text-[#374151]">
              {formik.values.isActive ? "Active" : "Inactive"}
            </span>
          </div>
        </FormRowVertical>
      )}

      <div className="mt-5 flex gap-3">
        <Button
          variant="orange"
          size="sm"
          isLoading={isChannelLoading}
          disabled={isButtonDisabled}
          onClick={() => formik.handleSubmit()}
        >
          {editingChannel ? "Update Channel" : "Add Channel"}
        </Button>
        <button
          type="button"
          className="text-[13px] font-medium text-[#6b7280] bg-transparent border-none cursor-pointer hover:text-[#111827]"
          onClick={closeDrawer}
          disabled={isChannelLoading}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  const priceDrawerContent = (
    <div className="px-4 py-4">
      {isLoadingPriceData ? (
        <div className="py-8">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-[#e5e7eb] overflow-hidden">
            <div className="grid grid-cols-[1fr_110px_130px] gap-3 px-3 py-2 bg-[#f8fafc] text-[12px] font-semibold text-[#475569]">
              <span>Item</span>
              <span>Base</span>
              <span>Channel Price</span>
            </div>
            <div className="max-h-[560px] overflow-auto">
              {menuItems.map((item) => (
                <div
                  key={item.correlationId}
                  className="grid grid-cols-[1fr_110px_130px] gap-3 items-center px-3 py-2.5 border-t border-[#e5e7eb] text-[13px]"
                >
                  <span className="min-w-0 truncate font-medium text-[#111827]">
                    {item.englishName || item.nonEnglishName || "Unnamed item"}
                  </span>
                  <span className="tabular-nums text-[#6b7280]">
                    {formatCentsToDollarsWithDollarSign(item.priceCents ?? 0)}
                  </span>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="h-8 w-full rounded-md border border-[#d1d5db] px-2 text-[13px] tabular-nums focus:outline-none focus:ring-2 focus:ring-[#fd7657]/30"
                    placeholder="Fallback"
                    value={priceDrafts[item.correlationId] ?? ""}
                    onChange={(event) => handlePriceDraftChange(item.correlationId, event.target.value)}
                  />
                </div>
              ))}
            </div>
          </div>
          {menuItems.length === 0 && (
            <p className="py-8 text-center text-[14px] text-[#6b7280]">No active menu items found.</p>
          )}
          <div className="mt-4 flex gap-3">
            <Button
              variant="orange"
              size="sm"
              isLoading={isSavingPrices}
              disabled={isSavingPrices}
              onClick={saveChannelItemPrices}
            >
              Save Prices
            </Button>
            <button
              type="button"
              className="text-[13px] font-medium text-[#6b7280] bg-transparent border-none cursor-pointer hover:text-[#111827]"
              onClick={closePriceDrawer}
              disabled={isSavingPrices}
            >
              Close
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <>
      <div className="w-full max-w-[900px]">
        <div className="bg-[#ffffff] rounded-[16px] px-6 py-5 shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_rgba(0,0,0,0.04)]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[18px] font-semibold text-[#111827]">Channel Management</h2>
            <Button variant="orange" size="sm" onClick={openAddDrawer}>
              Add New Channel
            </Button>
          </div>

          {isLoadingChannels ? (
            <div className="py-8"><Spinner /></div>
          ) : channels && channels.length > 0 ? (
            <div>
              <div className="flex items-center gap-6 mb-4 py-2.5 px-3 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] text-[13px]">
                <span className="font-medium text-[#475569]">
                  <span className="text-[#111827]">{channels.filter((c) => c.isActive !== false).length}</span> Active Channels
                </span>
                <span className="text-[#94a3b8]">|</span>
                <span className="text-[#475569]">
                  <span className="text-[#111827]">{menuItems.length}</span> Active Items
                </span>
              </div>
              <div className="rounded-[10px] border border-[#e5e7eb] overflow-hidden">
                {channels.map((channel: Channel) => (
                  <div
                    key={channel.correlationId}
                    className="flex items-start justify-between gap-3 px-4 py-3.5 bg-[#fafafa] odd:bg-[#ffffff] border-b border-[#d1d5db] last:border-b-0 hover:bg-[#f8fafc] transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-[14px] font-semibold text-[#111827] truncate">
                        {channel.name}
                      </div>
                      <div className="mt-1.5 flex items-center flex-wrap gap-4">
                        <span className="text-[13px] text-[#6b7280]">
                          Absolute item prices
                        </span>
                        <span
                          className={`shrink-0 text-[12px] font-medium px-2 py-0.5 rounded-full ${
                            (channel.isActive ?? true)
                              ? "bg-[#d1fae5] text-[#059669]"
                              : "bg-[#f3f4f6] text-[#6b7280]"
                          }`}
                        >
                          Status: {(channel.isActive ?? true) ? "Active" : "Inactive"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <button
                        type="button"
                        className="text-[13px] font-medium text-[#2563eb] bg-transparent border-none cursor-pointer hover:text-[#1d4ed8] focus:outline-none mt-0.5"
                        onClick={() => openPriceDrawer(channel)}
                      >
                        Prices
                      </button>
                      <button
                        type="button"
                        className="text-[13px] font-medium text-[#f97316] bg-transparent border-none cursor-pointer hover:text-[#ea580c] focus:outline-none mt-0.5"
                        onClick={() => handleEdit(channel)}
                        disabled={drawerOpen && editingChannel?.correlationId === channel?.correlationId}
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-[14px] text-[#6b7280] py-2">No channels found.</p>
          )}
        </div>
      </div>

      <OrderDetailsDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        title={editingChannel ? "Edit Channel" : "Add New Channel"}
        width={360}
      >
        {formContent}
      </OrderDetailsDrawer>

      <OrderDetailsDrawer
        open={!!priceDrawerChannel}
        onClose={closePriceDrawer}
        title={priceDrawerChannel ? `${priceDrawerChannel.name} Prices` : "Channel Prices"}
        width={720}
      >
        {priceDrawerContent}
      </OrderDetailsDrawer>
    </>
  );
};

export default ChannelTab;
